/*
* Copyright Gregory Coburn 2020-2026, All Rights Reserved, See license for further details
*/

import { inject, Injectable } from "@angular/core";
import { forkJoin, Observable, of } from "rxjs";
import { map } from "rxjs/operators";
import { AbstractObjectList } from "@src/app/model/abstract-object";
import { ImportParser } from '@src/app/modules/importer/import-page/Import-parser-interface';
import { ImportDoc } from "@src/app/modules/importer/ImportDoc";
import { GeneralParser } from "@src/app/modules/importer/General/GeneralParserService";
import { ParseOptionBase, ParseValidator } from "@src/app/modules/importer/General/ParseOptionBase";
import { POBValidators, validateEmailPOB } from "@src/app/modules/importer/General/POBValidators";
import { ImportField } from "@src/app/modules/importer/ImportField";
import { SupplierService } from "@src/app/modules/supply/supplier.service";
import { ImportRow } from "@src/app/modules/importer/ImportRow";
import { Supplier } from "@src/app/model/supplier";

@Injectable({
    providedIn: 'root'
})
export class SupplierParserService implements ImportParser {

    gps = new GeneralParser();
    supplierSvc = inject(SupplierService);
    suppliers : Supplier[] = []

    importDoc: ImportDoc;

    supplierNamePob = new ParseOptionBase('Supplier', 'name', [POBValidators.requiredPOB(), this.supplierNameValidator()]);
    codePOB = new ParseOptionBase('Code', 'code');
    addressPOB = new ParseOptionBase('Address', 'address');
    contactPersonPOB = new ParseOptionBase('Contact Person', 'contactPerson');
    phonePOB = new ParseOptionBase('Phone', 'phone');
    emailPOB = new ParseOptionBase('Email', 'email', [validateEmailPOB()]);
    accountsEmailPOB = new ParseOptionBase('Accounts Email', 'email', [validateEmailPOB()]);
    bankAccountNamePOB = new ParseOptionBase('Bank Account Name', 'bankAccountName');
    IBAN_POB = new ParseOptionBase('IBAN', 'IBAN');
    taxRegistrationNrPOB = new ParseOptionBase('Tax Registration', 'taxRegistrationNr');
    notesPOB = new ParseOptionBase('Notes', 'notes');

    availableImportItems = [
        this.supplierNamePob, this.codePOB, this.addressPOB, this.contactPersonPOB, this.phonePOB, this.emailPOB, this.accountsEmailPOB,
        this.bankAccountNamePOB, this.IBAN_POB, this.taxRegistrationNrPOB, this.notesPOB
    ];

    setUp() {
        this.importDoc = new ImportDoc();
        return forkJoin({
            suppliers: this.supplierSvc.get<Supplier>(),
        }).pipe(map(result => {
            this.suppliers = result.suppliers;
            return true;
        }));
    }

    findFuzzyMatches(supplierName: string): string {
        const stopWords = new Set([
            "ltd", "limited", "clg", "co", "company", "corporation", "corp", "llc", "plc",
            "the", "and", "&", "ireland", "irish", "group", "holdings",
            "partners", "international", "europe", "european", "teoranta", "services", "insurance", "plumbing", "roofing"
        ]);

        const words = supplierName.toLowerCase().split(/\s+/).filter(word => word.length > 1 && !stopWords.has(word));

        const matches = this.suppliers.filter(supplier => {
            const lowerName = supplier.name.toLowerCase();
            return words.some(word => lowerName.includes(word));
        });

        const names = matches.map(s => s.name);

        if (names.length === 0) return "";
        if (names.length === 1) return `[${names[0]}]`;
        if (names.length === 2) return `[${names[0]}] or [${names[1]}]`;
        const allButLast = names.slice(0, -1).join("], [");
        const last = names[names.length - 1];
        return `[${allButLast}] or [${last}]`;
    }

    getExactMatch(row: ImportRow, cellValue: string) : {how: string, exactMatch: Supplier} {
        let how = '';
        let exactMatch = this.suppliers.find( s => s.name.toLowerCase() === (cellValue || '').toLowerCase());
        const rowFullText = row.getFullText();
        if (exactMatch) {
            how = 'name';
        } else {
            this.suppliers.forEach ( s => {
                if (s.email && rowFullText.toLowerCase().includes(s.email)) {
                    how = 'email';
                    exactMatch = s;
                } else if (s.emailAccounts && rowFullText.toLowerCase().includes(s.emailAccounts)) {
                    how = 'emailAccounts';
                    exactMatch = s;
                } else if (s.web && rowFullText.toLowerCase().includes(s.web)) {
                    how = 'website';
                    exactMatch = s;
                }
            })
        }
        return {how, exactMatch}
    }

    supplierNameValidator() : ParseValidator {
        return {
            validateRow: (cell, row) => {
                const cellValue = cell.getValue() as string;
                const match = this.getExactMatch(row, cellValue)

                if (match.exactMatch) {
                    this.noteImportMatch(row, `${match.exactMatch.name} on ${match.how}`);
                    return 'Supplier already exists';
                }
                const matches = this.findFuzzyMatches(cellValue);
                if (matches) {
                    this.noteImportMatch(row, ` Possible Match: ${matches}`);
                }

                return null;
            }
        }
    }

    private noteImportMatch(row: ImportRow, matches: string) {
        let noteField = row.getField('possibleMatches');
        if (!noteField) {
            noteField = new ImportField('possibleMatches');
            row.add(noteField);
        }
        noteField.setValue(matches);
    }

    deleteRow(row:ImportRow) {
        this.importDoc.removeRow(row);
        this.importDoc.finalise();
        console.warn('Row removed from import', row);
    }

    parseRows(rows: unknown[][]): ImportDoc {
        this.gps.availableImportItems = this.availableImportItems;
        this.gps.parseRows(rows, this.importDoc);
        return this.importDoc;
    }

    postToServer(): Observable<AbstractObjectList> {
        const postItems = this.gps.getItemsToPost();
        console.log(postItems);

        if (!this.importDoc.hasErrors()) {
            return this.supplierSvc.postItems(postItems);
        }
        return of(null);

    }
}
